import { useMemo } from 'react';
import useAuth from '@sdk/authentication/useAuth';
import { TimesheetEntry } from '@sdk/timesheets/types/TimesheetEntry';
import { Shift } from '@sdk/automatedBreaks/types/Shift';
import { ClockInTime } from '@sdk/timeClock/utils/getActualClockInTimes';
import { getAutomatedBreaksSettingsForShift } from '@sdk/automatedBreaks/utils';

import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';

export const useEntriesBySequenceId = (timesheetEntries: TimesheetEntry[]) => {
  return useMemo(
    () =>
      timesheetEntries
        ? timesheetEntries.reduce((dict, entry) => {
            if (!dict[entry.baseSequenceId]) {
              dict[entry.baseSequenceId] = [];
            }

            dict[entry.baseSequenceId].push(entry);
            return dict;
          }, {})
        : {},
    [timesheetEntries]
  );
};

export const useActualRow = (shift: Shift, clockInTimesBefore: ClockInTime[]) => {
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const { currentUser } = useAuth();
  const { usTimeFormat, worldClockEnabled } = currentUser.settings;
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const automatedBreaksSettings = getAutomatedBreaksSettingsForShift(shift, automatedBreaks);
  const areAutomatedBreaksUsed = automatedBreaksFeatureEnabled && automatedBreaksSettings.isEnabled;
  const entriesBySequenceId = useEntriesBySequenceId(shift.timesheetEntries);
  const entriesBeforeBySequenceId = useEntriesBySequenceId(shift.timesheetEntriesBefore);
  const clockInTimesBeforeByBaseSequenceIdMap = (clockInTimesBefore || []).reduce(
    (dict, clockInTime) => {
      dict[clockInTime.baseSequenceId] = clockInTime;
      return dict;
    },
    {}
  );
  const clockInTimesOnlyBefore = (clockInTimesBefore || []).filter(
    (clockInTime) => !(clockInTime.baseSequenceId in entriesBySequenceId)
  );

  return {
    entriesBySequenceId,
    usTimeFormat,
    worldClockEnabled,
    areAutomatedBreaksUsed,
    clockInTimesOnlyBefore,
    clockInTimesBeforeByBaseSequenceIdMap,
    entriesBeforeBySequenceId,
  };
};
